<script setup lang="ts">
import { useProducts } from "@features/shopping/composables";
import { slugify } from "@shared/utils/slugify";
import { Dialog, DialogContent, DialogTrigger } from "@ui/dialog";
import { Input } from "@ui/input";
import { ScrollArea } from "@ui/scroll-area";
import { Search } from "lucide-vue-next";
import { ref, watch } from "vue";
import { RouterLink } from "vue-router";

const { search, searchResult } = useProducts();

const searchQuery = ref<string>("");
const loading = ref<boolean>(false);

const clearSearch = (status: boolean) => {
  if (!status) {
    searchQuery.value = "";
  }
};

watch(
  () => searchQuery.value,
  (term) => search(term),
);
</script>

<template>
  <Dialog @update:open="clearSearch">
    <DialogTrigger as-child>
      <img
        src="@assets/images/icons/icon-search.webp"
        width="20"
        height="20"
        class="cursor-pointer"
        alt="Open search dialog"
      />
    </DialogTrigger>
    <DialogContent
      class="max-h-[50vh] max-w-[90%] grid-rows-[auto_minmax(0,1fr)_auto] p-0 md:max-w-xl"
    >
      <div class="relative w-full items-center">
        <Input
          id="search"
          v-model="searchQuery"
          type="search"
          placeholder="Search products..."
          autocomplete="off"
          class="border-0 border-b pr-10 text-content placeholder:uppercase focus-visible:ring-0 focus-visible:ring-offset-0"
          list="search-results"
        />
        <span class="absolute inset-y-0 end-0 flex items-center justify-center px-2">
          <Search class="size-6 text-muted-foreground" />
        </span>
      </div>
      <ScrollArea as-child class="pl-2 pr-6">
        <datalist id="search-results">
          <div v-show="loading" class="text-content">Loading...</div>
          <div v-show="!loading && !searchQuery" class="text-content">
            Type something to search...
          </div>
          <RouterLink
            v-for="item in searchResult"
            v-show="searchQuery"
            :key="item.product_id"
            :to="`/product/${slugify(item.product_name)}`"
            class="mb-3 flex items-center gap-3 text-content"
          >
            <img
              v-show="item.product_image"
              :src="item.product_image"
              :alt="item.product_name"
              class="h-10 w-10 rounded-full"
            />
            <span>{{ item.product_name }}</span>
            <div class="text-xs ml-auto font-normal">{{ item.price_range || "N/A" }}</div>
          </RouterLink>
        </datalist>
      </ScrollArea>
    </DialogContent>
  </Dialog>
</template>
